import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AgencyModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, FetchSuccessPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/agency/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/agency/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/agency/FETCH_FAILURE'

export type AgencyModuleState = {
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
}

// Initial State
const initialState: AgencyModuleState = {
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(payload: FetchSuccessPayload = {}) {
  return {
    type: FETCH_SUCCESS,
    loadedIds: payload.loadedIds,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAgencies(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<AgencyModel>> => {
    const loadedForKeys = [...getState().agencies.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/agencies.json', options)
      .then(({ data }: { data: AgencyModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_ARRAY)
        dispatch(updateEntities(normalizedJson))

        const loadedIds = data.map(p => p.id)
        dispatch(fetchSuccess({ loadedIds }))

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadAgencyIds(agencyIds: number[], options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.agencies.loadedIds]
    const mergedIds = [...loadedIds, ...agencyIds]
    const uniqueIds = Array.from(new Set(mergedIds))

    const missingIds = agencyIds.filter(id => !loadedIds.includes(id))
    if (!missingIds.length){
      return new Promise((resolve) => {
        resolve({ success: true as const, data: [] })
      })
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds: uniqueIds }))

    const promise = api('/agencies.json', { ...options, provider_ids: JSON.stringify(missingIds) })
      .then(({ data }: { data: AgencyModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadAgency(agencyId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.agencies.loadedIds]

    if (agencyId && !loadedIds.includes(agencyId)){
      loadedIds.push(agencyId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/agencies/${agencyId}.json`, options)
      .then(({ data }: { data: AgencyModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AgencyModuleState = initialState,
  action: Action = { type: '' },
): AgencyModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds ? Array.from(new Set([...state.loadedIds, ...action.loadedIds])) : state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loaded: true,
        loadedIds: action.loadedIds ? Array.from(new Set([...state.loadedIds, ...action.loadedIds])) : state.loadedIds,
        loading: false,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
